/**
 * iter84 - Cost of Living Estimator.
 *
 * Standalone product page (NOT a dashboard bolt-on). See
 * /app/backend/cost_of_living.py for the honesty rules this page must respect:
 *
 *   - Every figure is a RANGE (no false-precision single numbers).
 *   - Every category and total carries a "last verified" date + sources.
 *   - Cities without founder-researched data render a "not yet verified"
 *     state - never an interpolated guess.
 *   - budget_tier persona data is used as a UI HINT (visual highlight),
 *     never to invent numbers we didn't source.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, MapPin, Info, Coins, ShieldCheck, Bus, Home, Utensils, Sparkles, Check } from "lucide-react";

const CATEGORY_ICON = {
  accommodation:    Home,
  student_dorm:     Home,
  groceries:        Utensils,
  health_insurance: ShieldCheck,
  transport:        Bus,
};

export default function CostEstimator() {
  const [mine,   setMine]   = useState(null);   // { verified, data?, budget_tier_range?, ...} | { verified:false, ... }
  const [cities, setCities] = useState(null);   // {cities:[...], count}
  const [pick,   setPick]   = useState(null);   // manually-selected city doc

  useEffect(() => {
    api.get("/cost/mine").then(r => setMine(r.data)).catch(() => setMine({ error: true }));
    api.get("/cost/cities").then(r => setCities(r.data)).catch(() => setCities({ error: true }));
  }, []);

  const active = pick || (mine?.verified ? mine.data : null);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-24" data-testid="cost-estimator-page">
      <div className="flex items-center justify-between gap-4">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted hover:text-ink" data-testid="cost-back-link">
          <ArrowLeft size={14}/> Back to dashboard
        </Link>
        <div className="text-[10px] uppercase tracking-widest text-elec font-bold">Cost of Living</div>
      </div>

      <h1 className="mt-6 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
        What a month in <span className="text-elec">{active ? active.city : "your host city"}</span> actually costs.
      </h1>
      <p className="mt-3 text-sm md:text-base text-ink/75 max-w-3xl leading-snug">
        Ranges only, cross-verified against Numbeo plus multiple host-country student cost guides.
        Every figure carries a "last verified" date and a source. We publish real numbers or nothing -
        no interpolated guesses, no glossy averages.
      </p>

      {/* --- CITY PICKER + PERSONALISED CTA -------------------------------- */}
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <MineCard mine={mine}/>
        <CityPickerCard cities={cities} onPick={async (name) => {
          if (!name) { setPick(null); return; }
          try {
            const r = await api.get(`/cost/city/${encodeURIComponent(name)}`);
            setPick(r.data);
          } catch { setPick(null); }
        }}/>
      </div>

      {/* --- MAIN VIEW ------------------------------------------------------ */}
      {active ? (
        <CityDetail
          doc={active}
          budgetTierRange={!pick && mine?.verified ? mine.budget_tier_range : null}
          budgetTierLabel={!pick && mine?.verified ? mine.budget_tier_label : null}
        />
      ) : (
        !mine ? (
          <div className="mt-10 moe-shimmer h-40 w-full"/>
        ) : mine.error ? (
          <div className="mt-10 moe-card p-6 text-sm text-muted italic" data-testid="cost-error">Couldn't load. Try refreshing.</div>
        ) : mine.verified === false ? (
          <NotVerifiedState mine={mine} cities={cities}/>
        ) : null
      )}
    </div>
  );
}

/* ── Personalised card - shows the logged-in student's own host city + budget tier ── */
function MineCard({ mine }) {
  if (!mine) return <div className="moe-shimmer h-28"/>;
  if (mine.error) return <div className="moe-card p-5 text-sm text-muted italic">Couldn't load your host city.</div>;

  const city = mine.host_city || "your host city";
  const country = mine.host_country || "";
  return (
    <div className="moe-panel-elec p-5 text-white" data-testid="cost-mine-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 font-bold">
        <MapPin size={12} strokeWidth={2.4}/> Your host city
      </div>
      <div className="mt-2 text-2xl font-bold leading-tight">
        {city}{country ? <span className="opacity-70 font-semibold">, {country}</span> : null}
      </div>
      {mine.verified ? (
        <div className="mt-3 text-[13px] leading-snug opacity-90">
          Real data below. Verified {mine.data?.last_verified}.
          {mine.budget_tier_label && <> Your personalised range is highlighted for <b>{mine.budget_tier_label}</b>.</>}
        </div>
      ) : (
        <div className="mt-3 text-[13px] leading-snug opacity-90" data-testid="cost-mine-unverified">
          Not yet verified for {city}. We're only publishing cities the founder has cross-referenced -
          Munich and Berlin ship first (Germany accounts for most of our current signups).
          More cities land in follow-up rounds.
        </div>
      )}
    </div>
  );
}

/* ── City picker - lets a student browse verified cities that aren't their own ── */
function CityPickerCard({ cities, onPick }) {
  const [value, setValue] = useState("");
  if (!cities) return <div className="moe-shimmer h-28"/>;
  if (cities.error) return <div className="moe-card p-5 text-sm text-muted italic">Couldn't load city list.</div>;
  return (
    <div className="moe-card p-5" data-testid="cost-picker-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted font-bold">
        <Coins size={12} strokeWidth={2.4}/> Browse a verified city
      </div>
      <div className="mt-2 text-sm text-ink/75">
        {cities.count} {cities.count === 1 ? "city" : "cities"} with real, cross-verified data.
      </div>
      <select
        data-testid="cost-city-picker"
        value={value}
        onChange={e => { setValue(e.target.value); onPick(e.target.value || null); }}
        className="mt-3 w-full moe-input">
        <option value="">— Pick a city —</option>
        {cities.cities.map(c => (
          <option key={c.city} value={c.city}>{c.city}, {c.country} · Tier {c.tier}</option>
        ))}
      </select>
      <div className="mt-3 text-[11px] text-muted leading-snug">
        We publish cities incrementally as the founder finishes real research. Extrapolating other
        German cities from Munich or Berlin numbers is off-limits.
      </div>
    </div>
  );
}

/* ── Full city detail - ranges, categories, sources, budget-tier highlight ── */
function CityDetail({ doc, budgetTierRange, budgetTierLabel }) {
  return (
    <div className="mt-10" data-testid={`cost-city-detail-${doc.city.toLowerCase()}`}>
      {/* Header strip */}
      <div className="flex flex-wrap items-baseline gap-3">
        <div className="text-2xl md:text-3xl font-bold">{doc.city}, {doc.country}</div>
        <span className="moe-chip moe-chip-yolk">Tier {doc.tier}</span>
        <span className="text-[11px] uppercase tracking-widest text-muted font-bold">Last verified {doc.last_verified}</span>
      </div>

      {/* National baseline (if present) */}
      {doc.national_baseline && (
        <div className="mt-5 moe-panel-yolk p-5" data-testid="cost-national-baseline">
          <div className="flex items-start gap-3">
            <Info size={18} strokeWidth={2.2} className="text-ink shrink-0 mt-0.5"/>
            <div className="text-sm text-ink leading-snug">
              <div className="font-bold">{doc.national_baseline.label}</div>
              <div className="mt-1">
                <b className="tabular-nums">{doc.currency} {doc.national_baseline.value_monthly.toLocaleString()}</b>
                <span className="text-ink/70"> / month</span>
                <span className="text-ink/50"> · </span>
                <b className="tabular-nums">{doc.currency} {doc.national_baseline.value_yearly.toLocaleString()}</b>
                <span className="text-ink/70"> / year</span>
              </div>
              <div className="mt-1 text-[13px] text-ink/80">{doc.national_baseline.note}</div>
            </div>
          </div>
        </div>
      )}

      {/* Total range - the headline number */}
      <div className="mt-6 moe-panel-elec p-6 text-white" data-testid="cost-total-range">
        <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Realistic monthly total</div>
        <div className="mt-2 text-3xl md:text-4xl font-bold tabular-nums">
          {doc.currency} {doc.total_range.min.toLocaleString()}–{doc.total_range.max.toLocaleString()}
          {doc.total_range.comfort_max && (
            <span className="opacity-70 text-2xl md:text-3xl"> (up to {doc.currency} {doc.total_range.comfort_max.toLocaleString()})</span>
          )}
          <span className="opacity-70 text-lg font-semibold"> /mo</span>
        </div>
        <div className="mt-3 text-[13px] opacity-90 leading-snug">{doc.total_range.note}</div>

        {budgetTierRange && (
          <div className="mt-4 rounded-xl bg-white/10 backdrop-blur px-4 py-3" data-testid="cost-budget-tier-highlight">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 font-bold">
              <Sparkles size={12}/> Personalised for your persona
            </div>
            <div className="mt-1 text-sm">
              {budgetTierLabel ? <>{budgetTierLabel} · </> : null}
              <b className="tabular-nums">{doc.currency} {budgetTierRange.min.toLocaleString()}–{budgetTierRange.max.toLocaleString()}</b>
              <span className="opacity-70"> /mo, based on the range above.</span>
            </div>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-elec font-bold">Category breakdown</div>
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          {doc.categories.map(cat => {
            const Icon = CATEGORY_ICON[cat.key] || Coins;
            return (
              <div key={cat.key} className="moe-card p-5" data-testid={`cost-category-${cat.key}`}>
                <div className="flex items-start gap-3">
                  <Icon size={20} strokeWidth={2.2} className="text-elec shrink-0 mt-0.5"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-ink">{cat.label}</div>
                    <div className="mt-1 text-lg font-bold tabular-nums">
                      {cat.min === cat.max
                        ? <>{doc.currency} {cat.min.toLocaleString()}</>
                        : <>{doc.currency} {cat.min.toLocaleString()}–{cat.max.toLocaleString()}</>
                      }
                      <span className="text-xs font-semibold text-muted ml-1">{cat.unit ? cat.unit.replace(`${doc.currency}/mo`, "/mo") : ""}</span>
                    </div>
                    {cat.note && <div className="mt-1 text-[12px] text-ink/70 leading-snug">{cat.note}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sources - the honesty layer */}
      <div className="mt-8 moe-card p-5" data-testid="cost-sources">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted font-bold">
          <Check size={12} strokeWidth={2.4}/> Sources
        </div>
        <div className="mt-2 text-[13px] text-ink/85 leading-snug">
          Sourced from Numbeo, cross-referenced against the guides below. Last verified <b>{doc.last_verified}</b>.
        </div>
        <ul className="mt-3 space-y-1 text-[13px] text-ink/85">
          {doc.sources.map((s, i) => <li key={i} className="flex gap-2"><span className="text-elec">·</span>{s}</li>)}
        </ul>
      </div>
    </div>
  );
}

/* ── Not-yet-verified state - shown when the student's host city has no data ── */
function NotVerifiedState({ mine, cities }) {
  return (
    <div className="mt-10 moe-card p-6 md:p-8" data-testid="cost-not-verified">
      <div className="flex items-start gap-4">
        <Info size={22} className="text-elec shrink-0 mt-0.5"/>
        <div className="min-w-0 flex-1">
          <div className="text-lg md:text-xl font-bold">Not yet verified for {mine.host_city || "your host city"}.</div>
          <div className="mt-2 text-sm text-ink/80 leading-snug max-w-2xl">
            {mine.message}
          </div>
          {cities?.cities?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Verified so far</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {cities.cities.map(c => (
                  <span key={c.city} className="moe-chip moe-chip-mint">{c.city}, {c.country}</span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 text-[12px] text-ink/60 italic">
            We publish real numbers or nothing. No interpolated guesses.
          </div>
        </div>
      </div>
    </div>
  );
}
